// ------------------------------------------------------------
// @project Pagali - Plataforma de Pagamentos
// @file KYC.tsx
// @author Arlinda Peixoto
// @company Pagali
// @description Verificação de Identidade (KYC) — wizard para utilizador
// @date 2026-04-22
// ------------------------------------------------------------

import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { bankingService } from '../services';
import { useToast } from '../contexts/ToastContext';
import type { KycStatus, KycDocument, AccountLimits } from '../types';
import {
  Shield,
  CheckCircle2,
  Clock,
  XCircle,
  Upload,
  Camera,
  MapPin,
  ChevronRight,
  AlertCircle,
  Info,
  FileText,
  User,
  Star,
  ArrowLeft,
  Loader2,
} from 'lucide-react';

// ── Helpers ─────────────────────────────────────────────────

const LEVEL_LABELS: Record<string, string> = {
  basic: 'Básico',
  standard: 'Standard',
  premium: 'Premium',
  business: 'Business',
};

const LEVEL_COLORS: Record<string, string> = {
  basic: 'text-gray-500 bg-gray-100',
  standard: 'text-blue-600 bg-blue-50',
  premium: 'text-purple-600 bg-purple-50',
  business: 'text-amber-600 bg-amber-50',
};

const DOC_LABELS: Record<string, string> = {
  id_front: 'BI / Cartão Cidadão (frente)',
  id_back: 'BI / Cartão Cidadão (verso)',
  passport: 'Passaporte',
  selfie: 'Selfie com documento',
  proof_of_address: 'Comprovativo de morada',
  proof_of_income: 'Comprovativo de rendimento',
};

const STATUS_ICON: Record<string, React.ReactNode> = {
  pending:  <Clock size={14} className="text-gray-400" />,
  uploaded: <Clock size={14} className="text-amber-500" />,
  verified: <CheckCircle2 size={14} className="text-green-500" />,
  rejected: <XCircle size={14} className="text-red-500" />,
};

function formatCVE(n: number) {
  return new Intl.NumberFormat('pt-PT', { maximumFractionDigits: 0 }).format(n) + ' ECV';
}

// ── Steps configuration ──────────────────────────────────────

interface WizardStep {
  id: string;
  label: string;
  icon: React.ReactNode;
  docTypes: string[];
  description: string;
  hint: string;
}

const WIZARD_STEPS: WizardStep[] = [
  {
    id: 'document',
    label: 'Documento de Identidade',
    icon: <FileText size={20} />,
    docTypes: ['id_front', 'id_back'],
    description: 'Fotografa ou carrega o teu BI / Cartão Cidadão (frente e verso).',
    hint: 'Certifica-te de que o documento está bem iluminado, sem reflexos e todos os dados legíveis.',
  },
  {
    id: 'selfie',
    label: 'Selfie',
    icon: <Camera size={20} />,
    docTypes: ['selfie'],
    description: 'Tira uma selfie segurando o teu documento de identidade visível.',
    hint: 'Fundo neutro, boa iluminação, rosto e documento bem visíveis na mesma fotografia.',
  },
  {
    id: 'address',
    label: 'Comprovativo de Morada',
    icon: <MapPin size={20} />,
    docTypes: ['proof_of_address'],
    description: 'Carrega uma fatura de água, luz ou declaração bancária com a tua morada.',
    hint: 'O documento não deve ter mais de 3 meses. O nome e morada devem coincidir com os dados do perfil.',
  },
];

// ── File upload area ─────────────────────────────────────────

interface UploadZoneProps {
  label: string;
  docType: string;
  existingDoc?: KycDocument;
  onUpload: (docType: string, file: File) => Promise<void>;
}

const UploadZone: React.FC<UploadZoneProps> = ({ label, docType, existingDoc, onUpload }) => {
  const [uploading, setUploading] = useState(false);
  const [preview, setPreview] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const handleFile = async (file: File) => {
    if (!file) return;
    const url = URL.createObjectURL(file);
    setPreview(url);
    setUploading(true);
    try {
      await onUpload(docType, file);
    } finally {
      setUploading(false);
    }
  };

  const isDone = existingDoc?.status === 'verified' || existingDoc?.status === 'uploaded';

  return (
    <div className="border-2 border-dashed rounded-xl p-4 transition-colors
      hover:border-purple-400 hover:bg-purple-50/30
      border-gray-200 bg-gray-50/50">

      <div className="flex items-center justify-between mb-2">
        <span className="text-sm font-medium text-gray-700">{label}</span>
        {existingDoc && (
          <span className="flex items-center gap-1 text-xs text-gray-500">
            {STATUS_ICON[existingDoc.status]}
            {existingDoc.status === 'verified' ? 'Verificado'
              : existingDoc.status === 'uploaded' ? 'Em análise'
              : existingDoc.status === 'rejected' ? 'Rejeitado'
              : 'Pendente'}
          </span>
        )}
      </div>

      {preview || isDone ? (
        <div className="relative">
          {preview && (
            <img src={preview} alt={label}
              className="w-full h-32 object-cover rounded-lg mb-2 border border-gray-200" />
          )}
          {isDone && !preview && (
            <div className="w-full h-16 rounded-lg bg-green-50 border border-green-200
              flex items-center justify-center gap-2 text-green-600 text-sm mb-2">
              <CheckCircle2 size={16} />
              Documento carregado
            </div>
          )}
          {existingDoc?.status === 'rejected' && (
            <p className="text-xs text-red-500 mb-2">
              Rejeitado: {existingDoc.rejectionReason || 'Documento inválido'}
            </p>
          )}
        </div>
      ) : null}

      <button
        onClick={() => inputRef.current?.click()}
        disabled={uploading || existingDoc?.status === 'verified'}
        className="w-full flex items-center justify-center gap-2 py-2 px-4 rounded-lg
          text-sm font-medium transition-colors
          bg-white border border-gray-200 text-gray-600
          hover:bg-purple-50 hover:border-purple-300 hover:text-purple-700
          disabled:opacity-50 disabled:cursor-not-allowed">
        {uploading ? (
          <><Loader2 size={14} className="animate-spin" /> A enviar...</>
        ) : existingDoc?.status === 'verified' ? (
          <><CheckCircle2 size={14} className="text-green-500" /> Verificado</>
        ) : (
          <><Upload size={14} /> {isDone ? 'Substituir' : 'Carregar ficheiro'}</>
        )}
      </button>

      <input
        ref={inputRef}
        type="file"
        accept="image/*,application/pdf"
        className="hidden"
        onChange={e => e.target.files?.[0] && handleFile(e.target.files[0])}
      />
    </div>
  );
};

// ── Main component ───────────────────────────────────────────

const KYC: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { showSuccess, showError } = useToast();

  const [loading, setLoading] = useState(true);
  const [kycStatus, setKycStatus] = useState<KycStatus | null>(null);
  const [limits, setLimits] = useState<AccountLimits | null>(null);
  const [activeStep, setActiveStep] = useState(0);
  const [submitting, setSubmitting] = useState(false);

  // ── Load initial data ─────────────────────────────────────

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [status, lim] = await Promise.all([
        bankingService.getKycStatus(),
        bankingService.getAccountLimits(),
      ]);
      setKycStatus(status);
      setLimits(lim);

      // Jump to first incomplete step
      const firstPending = WIZARD_STEPS.findIndex(step =>
        step.docTypes.some(dt => {
          const doc = status.documents.find(d => d.type === dt);
          return !doc || doc.status === 'rejected' || doc.status === 'pending';
        })
      );
      if (firstPending >= 0) setActiveStep(firstPending);
    } catch {
      // KYC not started yet — show step 0
    } finally {
      setLoading(false);
    }
  };

  // ── Upload handler ────────────────────────────────────────

  const handleUpload = async (docType: string, file: File) => {
    try {
      await bankingService.uploadKycDocument(docType, file);
      showSuccess('Documento enviado com sucesso');
      await loadData();
    } catch (err: any) {
      showError(err.message || 'Erro ao enviar documento');
      throw err;
    }
  };

  // ── Submit step ───────────────────────────────────────────

  const handleSubmitForReview = async () => {
    setSubmitting(true);
    try {
      await bankingService.startKycVerification('standard');
      showSuccess('Verificação submetida para análise!');
      await loadData();
    } catch (err: any) {
      showError(err.message || 'Erro ao submeter verificação');
    } finally {
      setSubmitting(false);
    }
  };

  // ── Computed ──────────────────────────────────────────────

  const getDocForType = (type: string) =>
    kycStatus?.documents.find(d => d.type === type);

  const stepComplete = (step: WizardStep) =>
    step.docTypes.every(dt => {
      const doc = getDocForType(dt);
      return doc && (doc.status === 'uploaded' || doc.status === 'verified');
    });

  const allStepsComplete = WIZARD_STEPS.every(stepComplete);
  const isApproved = kycStatus?.status === 'approved';
  const isUnderReview = kycStatus?.status === 'in_progress';

  // ── Render ────────────────────────────────────────────────

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <Loader2 size={32} className="animate-spin text-purple-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 pb-8">
      {/* Header */}
      <div className="bg-white border-b border-gray-100 px-4 py-4 sticky top-0 z-10">
        <div className="max-w-2xl mx-auto flex items-center gap-3">
          <button onClick={() => navigate(-1)}
            className="p-2 rounded-lg hover:bg-gray-100 text-gray-500">
            <ArrowLeft size={20} />
          </button>
          <div>
            <h1 className="text-lg font-semibold text-gray-900">Verificação de Identidade</h1>
            <p className="text-xs text-gray-500">KYC — Know Your Customer</p>
          </div>
          {kycStatus && (
            <span className={`ml-auto px-3 py-1 rounded-full text-xs font-semibold
              ${LEVEL_COLORS[kycStatus.level] || 'text-gray-500 bg-gray-100'}`}>
              {LEVEL_LABELS[kycStatus.level] || kycStatus.level}
            </span>
          )}
        </div>
      </div>

      <div className="max-w-2xl mx-auto px-4 pt-6 space-y-5">

        {/* Status banner */}
        {isApproved && (
          <div className="bg-green-50 border border-green-200 rounded-xl p-4
            flex items-center gap-3">
            <CheckCircle2 size={24} className="text-green-500 shrink-0" />
            <div>
              <p className="font-semibold text-green-800">Identidade verificada</p>
              <p className="text-sm text-green-600">
                A tua conta está totalmente verificada. Podes realizar transações sem limitações.
              </p>
            </div>
          </div>
        )}

        {isUnderReview && (
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-4
            flex items-center gap-3">
            <Clock size={24} className="text-amber-500 shrink-0" />
            <div>
              <p className="font-semibold text-amber-800">Verificação em análise</p>
              <p className="text-sm text-amber-600">
                Os teus documentos estão a ser analisados. Este processo demora 1-2 dias úteis.
              </p>
            </div>
          </div>
        )}

        {kycStatus?.status === 'rejected' && (
          <div className="bg-red-50 border border-red-200 rounded-xl p-4
            flex items-start gap-3">
            <XCircle size={24} className="text-red-500 shrink-0 mt-0.5" />
            <div>
              <p className="font-semibold text-red-800">Verificação rejeitada</p>
              <p className="text-sm text-red-600">
                {kycStatus.rejectionReason || 'Por favor corrige os documentos indicados e resubmete.'}
              </p>
            </div>
          </div>
        )}

        {/* Limits card */}
        {limits && (
          <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-4">
            <div className="flex items-center gap-2 mb-3">
              <Star size={16} className="text-purple-500" />
              <h3 className="text-sm font-semibold text-gray-700">Limites da tua conta</h3>
            </div>
            <div className="grid grid-cols-3 gap-3">
              {[
                { label: 'Transação', value: limits.transactionLimits.perTransaction },
                { label: 'Diário', value: limits.transactionLimits.daily },
                { label: 'Mensal', value: limits.transactionLimits.monthly },
              ].map(({ label, value }) => (
                <div key={label} className="bg-gray-50 rounded-lg p-2 text-center">
                  <p className="text-xs text-gray-500 mb-1">{label}</p>
                  <p className="text-sm font-bold text-gray-800">{formatCVE(value)}</p>
                </div>
              ))}
            </div>
            {!isApproved && (
              <p className="text-xs text-gray-400 mt-3 flex items-center gap-1">
                <Info size={12} />
                Completa a verificação para aumentar os teus limites
              </p>
            )}
          </div>
        )}

        {/* Progress steps */}
        <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
          {/* Step tabs */}
          <div className="flex border-b border-gray-100">
            {WIZARD_STEPS.map((step, i) => {
              const done = stepComplete(step);
              return (
                <button
                  key={step.id}
                  onClick={() => setActiveStep(i)}
                  className={`flex-1 flex flex-col items-center gap-1 py-3 px-2 text-xs
                    transition-colors border-b-2
                    ${activeStep === i
                      ? 'border-purple-500 text-purple-700 font-semibold'
                      : 'border-transparent text-gray-400 hover:text-gray-600'}`}>
                  <span className={`w-6 h-6 rounded-full flex items-center justify-center
                    text-xs font-bold
                    ${done ? 'bg-green-100 text-green-600'
                      : activeStep === i ? 'bg-purple-100 text-purple-700'
                      : 'bg-gray-100 text-gray-400'}`}>
                    {done ? <CheckCircle2 size={14} /> : i + 1}
                  </span>
                  <span className="hidden sm:block">{step.label}</span>
                </button>
              );
            })}
          </div>

          {/* Active step content */}
          {WIZARD_STEPS.map((step, i) => (
            <div key={step.id} className={activeStep === i ? 'block' : 'hidden'}>
              <div className="p-4">
                {/* Step header */}
                <div className="flex items-start gap-3 mb-4">
                  <div className="p-2 bg-purple-50 rounded-lg text-purple-600 shrink-0">
                    {step.icon}
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900">{step.label}</h3>
                    <p className="text-sm text-gray-500 mt-0.5">{step.description}</p>
                  </div>
                </div>

                {/* Hint */}
                <div className="bg-blue-50 border border-blue-100 rounded-lg p-3 mb-4
                  flex items-start gap-2">
                  <AlertCircle size={14} className="text-blue-500 shrink-0 mt-0.5" />
                  <p className="text-xs text-blue-700">{step.hint}</p>
                </div>

                {/* Upload zones */}
                <div className="space-y-3">
                  {step.docTypes.map(dt => (
                    <UploadZone
                      key={dt}
                      label={DOC_LABELS[dt] || dt}
                      docType={dt}
                      existingDoc={getDocForType(dt)}
                      onUpload={handleUpload}
                    />
                  ))}
                </div>

                {/* Navigation */}
                <div className="flex justify-between mt-4 pt-4 border-t border-gray-100">
                  <button
                    onClick={() => setActiveStep(Math.max(0, i - 1))}
                    disabled={i === 0}
                    className="px-4 py-2 text-sm text-gray-500 hover:text-gray-700
                      disabled:opacity-0 transition-opacity">
                    ← Anterior
                  </button>

                  {i < WIZARD_STEPS.length - 1 ? (
                    <button
                      onClick={() => setActiveStep(i + 1)}
                      className="flex items-center gap-1 px-4 py-2 bg-purple-600 text-white
                        rounded-lg text-sm font-medium hover:bg-purple-700 transition-colors">
                      Próximo <ChevronRight size={14} />
                    </button>
                  ) : (
                    <button
                      onClick={handleSubmitForReview}
                      disabled={!allStepsComplete || submitting || isUnderReview || isApproved}
                      className="flex items-center gap-2 px-5 py-2 bg-purple-600 text-white
                        rounded-lg text-sm font-semibold hover:bg-purple-700 transition-colors
                        disabled:opacity-50 disabled:cursor-not-allowed">
                      {submitting ? (
                        <><Loader2 size={14} className="animate-spin" /> A enviar...</>
                      ) : isApproved ? (
                        <><CheckCircle2 size={14} /> Verificado</>
                      ) : isUnderReview ? (
                        <><Clock size={14} /> Em análise</>
                      ) : (
                        <><Shield size={14} /> Submeter para verificação</>
                      )}
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* What happens next */}
        {!isApproved && (
          <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-4">
            <h3 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
              <Info size={14} className="text-purple-500" />
              Como funciona a verificação
            </h3>
            <div className="space-y-3">
              {[
                { step: '1', text: 'Carregas os teus documentos de identidade.' },
                { step: '2', text: 'A equipa Pagali analisa os documentos em 1-2 dias úteis.' },
                { step: '3', text: 'Recebes uma notificação com o resultado.' },
                { step: '4', text: 'Com KYC aprovado, os teus limites de transação aumentam automaticamente.' },
              ].map(({ step, text }) => (
                <div key={step} className="flex items-start gap-3">
                  <span className="w-5 h-5 rounded-full bg-purple-100 text-purple-700
                    text-xs font-bold flex items-center justify-center shrink-0">
                    {step}
                  </span>
                  <p className="text-sm text-gray-600">{text}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Documents summary */}
        {kycStatus && kycStatus.documents.length > 0 && (
          <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-4">
            <h3 className="text-sm font-semibold text-gray-700 mb-3">
              Documentos submetidos
            </h3>
            <div className="space-y-2">
              {kycStatus.documents.map((doc, i) => (
                <div key={i} className="flex items-center justify-between py-2
                  border-b border-gray-50 last:border-0">
                  <span className="text-sm text-gray-600">
                    {DOC_LABELS[doc.type] || doc.type}
                  </span>
                  <span className="flex items-center gap-1 text-xs">
                    {STATUS_ICON[doc.status]}
                    <span className={
                      doc.status === 'verified' ? 'text-green-600'
                      : doc.status === 'rejected' ? 'text-red-500'
                      : 'text-amber-500'
                    }>
                      {doc.status === 'verified' ? 'Verificado'
                        : doc.status === 'uploaded' ? 'Em análise'
                        : doc.status === 'rejected' ? 'Rejeitado'
                        : 'Pendente'}
                    </span>
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}

      </div>
    </div>
  );
};

export default KYC;
